import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { IAssistantChange, IAssistantMessage } from '../assistant.models';

interface IChangeRow{
	label: string;
	before?: string;
	after: string;
}

interface IChangeView{
	target: string;
	period?: string;
	priceBefore?: string;
	priceAfter?: string;
	rows: IChangeRow[];
	note?: string;
	/** regola che la proposta elimina: periodo e prezzo barrati */
	removed?: boolean;
	/** testi di vendita di una lingua: resi da assistant-text-change */
	textChange?: IAssistantChange;
}

const FIELD_LABELS: Record<string, string> = {
	valid_from: 'Viaggi dal',
	valid_to: 'Viaggi fino al',
	booking_from: 'Prenotazioni dal',
	booking_to: 'Prenotazioni fino al',
	price: 'Prezzo',
	pickup_point: 'Punto di ritiro',
	descrizione: 'Nome',
	// update_package_classification
	destinations: 'Destinazioni',
	categories: 'Categorie',
	pacchetti: 'Pacchetti',
	'visibile su sito e widget': 'Visibile su sito e widget',
	codice: 'Codice',
	'regola partecipante': 'Categoria',
	fiscalità: 'Fiscalità',
	// fasce d'età del pacchetto (PackageAgeBands)
	ADT: 'Adulti',
	CHD: 'Bambini',
	INF: 'Infant',
	SNR: 'Senior'
};

/** ordine di visualizzazione dei campi (il server li manda in jsonb, senza ordine) */
const FIELD_ORDER = [
	'valid_from', 'valid_to', 'booking_from', 'booking_to', 'price', 'pickup_point',
	// regole di allotment, Staff, Calendario Staff, Campi aggiuntivi (step Avanzate della risorsa)
	'Persona', 'Dal', 'Al', 'Posti', 'Giorni assegnati', 'Tipo', 'Nota', 'Domanda', 'In inglese',
	'descrizione', 'codice', 'regola partecipante', 'fiscalità'
];

/**
 * Proposta di modifica da confermare, resa come cartellino prezzo: cosa cambia sopra, decisione sotto lo strappo.
 * Le creazioni di regole mostrano periodo + prezzo (vecchio barrato se è una variazione); le modifiche campo per campo.
 */
@Component({
	selector: 'assistant-action-card',
	templateUrl: './action-card.component.html',
	styleUrls: ['./action-card.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AssistantActionCardComponent{

	static readonly COLLAPSED_LIMIT = 4;

	@Input() set item(item: IAssistantMessage){
		this._item = item;
		const changes = item.action?.summary.changes || [];
		this.changes = changes.map(change => AssistantActionCardComponent.view(change));
		this.showTargets = new Set(changes.map(change => change.target)).size > 1;
	}
	get item(): IAssistantMessage{ return this._item; }

	@Output() confirm = new EventEmitter<IAssistantMessage>();
	@Output() cancel = new EventEmitter<IAssistantMessage>();

	changes: IChangeView[] = [];
	/** più criteri nella stessa proposta: serve il nome su ogni riga, altrimenti basta il titolo */
	showTargets = false;
	expanded = false;
	private _item: IAssistantMessage;

	get visibleChanges(): IChangeView[]{
		return this.expanded ? this.changes : this.changes.slice(0, AssistantActionCardComponent.COLLAPSED_LIMIT);
	}

	get hiddenCount(): number{
		return this.expanded ? 0 : Math.max(0, this.changes.length - AssistantActionCardComponent.COLLAPSED_LIMIT);
	}

	get status(): string{
		return this.item.action?.status || 'pending';
	}

	/** proposta che elimina soltanto: il pulsante dice "Elimina" */
	get onlyRemovals(): boolean{
		return this.changes.length > 0 && this.changes.every(change => change.removed);
	}

	/** valid_to arriva già formattato dal server: "gg/mm/aaaa" oppure "senza scadenza" */
	private static period(fields: Record<string, string>): string{
		const hasEnd = /^\d{2}\/\d{2}\/\d{4}$/.test(fields.valid_to || '');
		if(hasEnd && fields.valid_to === fields.valid_from) return 'Solo il ' + fields.valid_from;
		if(hasEnd) return 'Dal ' + fields.valid_from + ' al ' + fields.valid_to;
		return 'Dal ' + fields.valid_from + ', senza scadenza';
	}

	private static bookingWindow(fields: Record<string, string>): string | null{
		if(!fields.booking_from && !fields.booking_to) return null;
		return [fields.booking_from && 'dal ' + fields.booking_from, fields.booking_to && 'fino al ' + fields.booking_to].filter(Boolean).join(' ');
	}

	static view(change: IAssistantChange): IChangeView{
		const after = change.after || {};
		const before = change.before || {};
		const isPriceRuleCreation = change.op === 'create' && 'valid_from' in after;

		if(change.kind === 'texts') return { target: change.target, rows: [], textChange: change, note: change.note };
		// campi compilati nel form dello step (fill_resource_form): solo il valore proposto
		if(change.kind === 'form') return { target: change.target, rows: (change.fields || []).map(field => ({ label: field.label, after: field.after })), note: change.note };

		if(change.op === 'delete'){
			const rows: IChangeRow[] = [];
			const booking = AssistantActionCardComponent.bookingWindow(before);
			if(booking) rows.push({ label: 'Per prenotazioni', before: booking, after: '—' });
			if(before.pickup_point) rows.push({ label: FIELD_LABELS.pickup_point, before: before.pickup_point, after: '—' });
			return {
				target: change.target,
				period: before.valid_from ? AssistantActionCardComponent.period(before) : undefined,
				priceBefore: before.price,
				rows,
				note: change.note,
				removed: true
			};
		}

		if(isPriceRuleCreation){
			const rows: IChangeRow[] = [];
			const booking = AssistantActionCardComponent.bookingWindow(after);
			if(booking) rows.push({ label: 'Per prenotazioni', after: booking });
			// regola valida solo per un punto di ritiro (risorse Pick-up)
			if(after.pickup_point) rows.push({ label: FIELD_LABELS.pickup_point, after: after.pickup_point });
			return {
				target: change.target,
				period: AssistantActionCardComponent.period(after),
				priceBefore: before.price,
				priceAfter: after.price,
				rows,
				note: change.note
			};
		}

		const keys = AssistantActionCardComponent.orderedKeys([...Object.keys(before), ...Object.keys(after)]);
		return {
			target: change.target,
			// valore invariato: solo il valore, senza barrato
			rows: keys.map(key => ({ label: FIELD_LABELS[key] || key, before: before[key] !== after[key] ? before[key] : undefined, after: after[key] ?? '—' })),
			note: change.note
		};
	}

	/**
	 * Il riepilogo è salvato in jsonb, che riordina le chiavi (per lunghezza): "Al" prima di "Dal", valid_to prima di
	 * valid_from. Ordine fisso per i campi noti, gli altri dopo nell'ordine ricevuto.
	 */
	static orderedKeys(keys: string[]): string[]{
		const unique = Array.from(new Set(keys));
		const known = FIELD_ORDER.filter(key => unique.includes(key));
		return [...known, ...unique.filter(key => !FIELD_ORDER.includes(key))];
	}
}
